import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Zap, ChevronLeft, RefreshCw, Activity, Users, MousePointerClick, Sparkles, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => {
  const token = localStorage.getItem("lf_session");
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
};

const TABS = [
  { id: "builtin", label: "Built-in" },
  { id: "posthog", label: "PostHog (native)" },
  { id: "embed", label: "PostHog (embed)" },
];

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-zinc-500 font-semibold uppercase tracking-wide">
        <Icon size={12} /> {label}
      </div>
      <div className="text-3xl font-bold text-zinc-950 mt-2">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("builtin");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [recent, setRecent] = useState([]);
  const [phStatus, setPhStatus] = useState(null);
  const [phEventCounts, setPhEventCounts] = useState([]);
  const [phPageviews, setPhPageviews] = useState([]);
  const [phRecent, setPhRecent] = useState([]);
  const [phLoading, setPhLoading] = useState(false);
  const [phError, setPhError] = useState(null);

  const loadBuiltin = async () => {
    setLoading(true);
    try {
      const [ov, ts, rc] = await Promise.all([
        axiosAuth().get("/admin/analytics/overview"),
        axiosAuth().get("/admin/analytics/timeseries?days=30"),
        axiosAuth().get("/admin/analytics/recent-events?limit=50"),
      ]);
      setOverview(ov.data);
      setSeries((ts.data?.series || []).map((r) => ({ day: r.day, count: r.count })));
      setRecent(rc.data?.events || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  const loadPosthog = async () => {
    setPhLoading(true);
    setPhError(null);
    try {
      const status = await axiosAuth().get("/admin/analytics/posthog/status");
      setPhStatus(status.data);
      if (status.data.ready) {
        const [counts, pv, rec] = await Promise.all([
          axiosAuth().get("/admin/analytics/posthog/event-counts?days=7"),
          axiosAuth().get("/admin/analytics/posthog/daily-pageviews?days=30"),
          axiosAuth().get("/admin/analytics/posthog/recent-events?limit=20&hours_back=72"),
        ]);
        setPhEventCounts(counts.data?.rows || []);
        setPhPageviews(pv.data?.rows || []);
        setPhRecent((rec.data?.results || []).slice(0, 20));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not load PostHog data";
      setPhError(msg);
    } finally {
      setPhLoading(false);
    }
  };

  useEffect(() => { loadBuiltin(); }, []);
  useEffect(() => { if (tab === "posthog") loadPosthog(); else if (tab === "embed" && !phStatus) loadPosthog(); }, [tab]);

  const totals = overview?.totals || {};
  const funnel = overview?.funnel_lifetime || {};
  const topEvents = overview?.top_events_30d || [];
  const topPromoters = overview?.top_promoter_codes_30d || [];

  const conversion = useMemo(() => {
    if (!funnel.projects_submitted) return null;
    return {
      proposalRate: funnel.projects_submitted ? (funnel.proposals_generated / funnel.projects_submitted) * 100 : 0,
      depositRate: funnel.projects_submitted ? (funnel.deposits_paid / funnel.projects_submitted) * 100 : 0,
      deliveryRate: funnel.deposits_paid ? (funnel.delivered / funnel.deposits_paid) * 100 : 0,
    };
  }, [funnel]);

  if (loading && !overview) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-analytics-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="p-1.5 rounded-lg hover:bg-zinc-100" data-testid="btn-analytics-back" aria-label="Back">
            <ChevronLeft size={18} />
          </button>
          <BrandLogo variant="mark" size={32} />
          <div>
            <div className="font-bold text-zinc-900">Analytics</div>
            <div className="text-xs text-zinc-500">In-house funnel + PostHog product analytics.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={tab === "builtin" ? loadBuiltin : loadPosthog}
            data-testid="btn-analytics-refresh"
            className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={() => navigate("/admin/settings")}
            data-testid="btn-analytics-go-settings"
            className="text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50"
          >
            Settings
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-6 border-b border-zinc-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ---- Built-in tab ---- */}
        {tab === "builtin" && (
          <div className="space-y-6" data-testid="builtin-content">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard icon={Activity} label="Events 24h" value={totals.events_24h ?? 0} testId="kpi-events-24h" />
              <StatCard icon={Activity} label="Events 7d" value={totals.events_7d ?? 0} testId="kpi-events-7d" />
              <StatCard icon={Activity} label="Events 30d" value={totals.events_30d ?? 0} testId="kpi-events-30d" />
              <StatCard icon={Users} label="Sessions 30d" value={totals.sessions_30d ?? 0} testId="kpi-sessions-30d" />
              <StatCard icon={MousePointerClick} label="Anon visitors 30d" value={totals.unique_anonymous_30d ?? 0} testId="kpi-anon-30d" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-zinc-900">Daily events (30 days)</h3>
                  <span className="text-xs text-zinc-500">In-house tracker</span>
                </div>
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <LineChart data={series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                      <Line type="monotone" dataKey="count" stroke="#09090B" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                <h3 className="font-semibold text-zinc-900 mb-4">Conversion funnel (lifetime)</h3>
                <div className="space-y-3 text-sm">
                  <FunnelRow label="Projects submitted" value={funnel.projects_submitted ?? 0} percent={100} />
                  <FunnelRow label="Proposals generated" value={funnel.proposals_generated ?? 0} percent={conversion?.proposalRate} />
                  <FunnelRow label="Deposits paid" value={funnel.deposits_paid ?? 0} percent={conversion?.depositRate} />
                  <FunnelRow label="Delivered" value={funnel.delivered ?? 0} percent={conversion?.deliveryRate ? conversion.deliveryRate * (funnel.deposits_paid / (funnel.projects_submitted || 1)) : 0} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                <h3 className="font-semibold text-zinc-900 mb-4">Top events (30 days)</h3>
                {topEvents.length === 0 ? (
                  <div className="text-sm text-zinc-500">No events recorded yet.</div>
                ) : (
                  <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer>
                      <BarChart data={topEvents.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="event" tick={{ fontSize: 11 }} width={110} />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="count" fill="#09090B" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                <h3 className="font-semibold text-zinc-900 mb-4">Top promoter codes (30 days)</h3>
                {topPromoters.length === 0 ? (
                  <div className="text-sm text-zinc-500">No promoter-attributed events yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-100">
                        <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Code</th>
                        <th className="text-right py-2 text-xs uppercase tracking-wide text-zinc-500">Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPromoters.map((p) => (
                        <tr key={p.promoter_code} className="border-b border-zinc-50">
                          <td className="py-2 font-mono text-xs">{p.promoter_code}</td>
                          <td className="py-2 text-right">{p.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="recent-events-card">
              <h3 className="font-semibold text-zinc-900 mb-4">Recent events</h3>
              {recent.length === 0 ? (
                <div className="text-sm text-zinc-500">No events yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-100">
                        <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Time</th>
                        <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Event</th>
                        <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Path</th>
                        <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Anon ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((e) => (
                        <tr key={e.event_id} className="border-b border-zinc-50">
                          <td className="py-2 font-mono text-xs text-zinc-500">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                          <td className="py-2 font-medium text-zinc-800">{e.event_name}</td>
                          <td className="py-2 text-xs text-zinc-500 truncate max-w-[280px]">{e.path || "—"}</td>
                          <td className="py-2 font-mono text-xs text-zinc-500">{(e.anonymous_id || "").slice(0, 14)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- PostHog native tab ---- */}
        {tab === "posthog" && (
          <div className="space-y-6" data-testid="posthog-native-content">
            <PostHogStatusBadge status={phStatus} />
            {phLoading && (
              <div className="flex items-center justify-center py-12 text-zinc-400">
                <Loader2 size={20} className="animate-spin mr-2" /> Loading PostHog…
              </div>
            )}
            {phError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2" data-testid="posthog-error">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">PostHog server-side fetch failed</div>
                  <div>{phError}</div>
                  <div className="mt-2 text-xs">
                    Configure your <strong>Personal API Key</strong> and <strong>Project ID</strong> under{" "}
                    <button onClick={() => navigate("/admin/settings")} className="underline">
                      Settings
                    </button>.
                  </div>
                </div>
              </div>
            )}

            {phStatus?.ready && !phError && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                    <h3 className="font-semibold text-zinc-900 mb-4">Top events (PostHog, 7 days)</h3>
                    {phEventCounts.length === 0 ? (
                      <div className="text-sm text-zinc-500">No PostHog events yet (events take a few seconds to be queryable).</div>
                    ) : (
                      <div style={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                          <BarChart data={phEventCounts.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                            <XAxis type="number" tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="event" tick={{ fontSize: 11 }} width={110} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div className="bg-white border border-zinc-200 rounded-2xl p-6">
                    <h3 className="font-semibold text-zinc-900 mb-4">Daily pageviews (PostHog, 30 days)</h3>
                    {phPageviews.length === 0 ? (
                      <div className="text-sm text-zinc-500">No pageview data yet.</div>
                    ) : (
                      <div style={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                          <LineChart data={phPageviews}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Line type="monotone" dataKey="pageviews" stroke="#3B82F6" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="posthog-recent-events">
                  <h3 className="font-semibold text-zinc-900 mb-4">Recent PostHog events (last 72h)</h3>
                  {phRecent.length === 0 ? (
                    <div className="text-sm text-zinc-500">No recent events captured by PostHog.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-100">
                            <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Time</th>
                            <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Event</th>
                            <th className="text-left py-2 text-xs uppercase tracking-wide text-zinc-500">Distinct ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phRecent.map((e) => (
                            <tr key={e.id || e.uuid} className="border-b border-zinc-50">
                              <td className="py-2 font-mono text-xs text-zinc-500">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                              <td className="py-2 font-medium text-zinc-800">{e.event}</td>
                              <td className="py-2 font-mono text-xs text-zinc-500">{(e.distinct_id || "").slice(0, 24)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ---- Embed tab ---- */}
        {tab === "embed" && (
          <div className="space-y-4" data-testid="posthog-embed-content">
            <PostHogStatusBadge status={phStatus} />
            {!phStatus?.embed_url ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
                <div className="flex items-start gap-2">
                  <Sparkles size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold mb-1">No PostHog dashboard linked yet</div>
                    <div>
                      In PostHog, open any dashboard → click <strong>Share</strong> → toggle <strong>Public</strong> → copy the URL. Then save it as
                      <span className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded ml-1">POSTHOG_DASHBOARD_EMBED_URL</span> under{" "}
                      <button onClick={() => navigate("/admin/settings")} className="underline">Settings</button>.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span>Embedded PostHog dashboard</span>
                  <a href={phStatus.embed_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-zinc-900" data-testid="link-open-embed">
                    Open in PostHog <ExternalLink size={12} />
                  </a>
                </div>
                <iframe
                  data-testid="posthog-iframe"
                  title="PostHog Dashboard"
                  src={phStatus.embed_url}
                  style={{ width: "100%", height: "calc(100vh - 230px)", border: "none", borderRadius: 12, background: "white" }}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelRow({ label, value, percent }) {
  const pct = Math.max(0, Math.min(100, percent || 0));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-zinc-700">{label}</span>
        <span className="font-mono text-xs text-zinc-500">
          {value.toLocaleString()} {percent !== undefined && pct > 0 ? `· ${pct.toFixed(1)}%` : ""}
        </span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full bg-zinc-950 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PostHogStatusBadge({ status }) {
  if (!status) return null;
  const ready = status.ready;
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${
        ready ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
      }`}
      data-testid="posthog-status-badge"
    >
      {ready ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
      <span>
        {ready
          ? `PostHog connected (${status.host})`
          : `PostHog not fully configured · key=${status.has_personal_api_key ? "✓" : "✗"} · project_id=${status.has_project_id ? "✓" : "✗"}`}
      </span>
    </div>
  );
}
